import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ensureMetaPixelLoaded, trackPageView } from '../utils/metaPixel';

const GTM_CONTAINER_ID = 'GTM-M2SGZFW';
const GA_MEASUREMENT_ID = 'G-3QEF8XGR51';

let gaBootstrapped = false;

function ensureGaLoaded() {
  if (typeof window === 'undefined') return;

  // Ensure dataLayer + gtag stub exist (queues calls until gtag.js loads)
  window.dataLayer = window.dataLayer || [];
  window.gtag =
    window.gtag ||
    function gtag() {
      // eslint-disable-next-line prefer-rest-params
      window.dataLayer!.push(arguments);
    };

  // Inject gtag.js only once per page load.
  const existingScript = document.querySelector<HTMLScriptElement>(
    `script[data-ga-measurement-id="${GA_MEASUREMENT_ID}"]`,
  );

  if (!existingScript) {
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`;
    script.setAttribute('data-ga-measurement-id', GA_MEASUREMENT_ID);
    document.head.appendChild(script);
  }

  // Guard against React StrictMode double-invocation in development.
  if (!gaBootstrapped) {
    window.gtag('js', new Date());
    gaBootstrapped = true;
  }
}

/**
 * Loads Google Tag Manager only when mounted.
 * Intended to be rendered only on the `/n2n/*` route.
 */
export default function N2NAnalytics() {
  const location = useLocation();

  useEffect(() => {
    // Inject GTM once.
    const existingGtm = document.querySelector<HTMLScriptElement>(
      `script[data-gtm-container="${GTM_CONTAINER_ID}"]`,
    );

    if (!existingGtm) {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({
        'gtm.start': new Date().getTime(),
        event: 'gtm.js',
      });

      const script = document.createElement('script');
      script.async = true;
      script.src = `https://www.googletagmanager.com/gtm.js?id=${GTM_CONTAINER_ID}`;
      script.setAttribute('data-gtm-container', GTM_CONTAINER_ID);
      document.head.appendChild(script);
    }
  }, []);

  // Load GA script when entering /n2n.
  useEffect(() => {
    if (!location.pathname.startsWith('/n2n')) return;
    ensureGaLoaded();
  }, [location.pathname]);

  // Load Meta Pixel only for /n2n and initialize it once.
  useEffect(() => {
    if (!location.pathname.startsWith('/n2n')) return;
    ensureMetaPixelLoaded();
  }, [location.pathname]);

  // Track SPA navigations for /n2n/*.
  useEffect(() => {
    if (!location.pathname.startsWith('/n2n')) return;
    ensureGaLoaded();

    // `config` triggers a page_view by default in GA4.
    const page_path = `${location.pathname}${location.search}`;
    window.gtag?.('config', GA_MEASUREMENT_ID, { page_path });
  }, [location.pathname, location.search]);

  // Track SPA navigations for /n2n/*.
  useEffect(() => {
    if (!location.pathname.startsWith('/n2n')) return;

    // Track SPA navigation as PageView in Meta Pixel too.
    trackPageView();
  }, [location.pathname, location.search]);

  return null;
}
